import * as React from "react";
import { gsap } from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { CustomWiggle } from "gsap/CustomWiggle";
import { cn } from "@/lib/utils";

gsap.registerPlugin(CustomEase, CustomWiggle);

const WIGGLE_EASE_NAME = "wavyTextWiggle";
const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

export type WavyTextProps = {
  text: string;
  className?: string;
  duration?: number;
  amplitude?: number;
  opacity?: number;
  stagger?: number;
  repeat?: number;
  wiggles?: number;
  wiggleType?: "easeOut" | "easeInOut";
};

export function WavyText({
  text,
  className,
  duration = 1.5,
  amplitude = 12,
  opacity = 1,
  stagger = 0.04,
  repeat = -1,
  wiggles = 1,
  wiggleType = "easeInOut",
}: WavyTextProps) {
  const containerRef = React.useRef<HTMLParagraphElement>(null);
  const charRefs = React.useRef<HTMLSpanElement[]>([]);
  const easeName = React.useId() ?? WIGGLE_EASE_NAME;
  const characters = React.useMemo(() => Array.from(text), [text]);

  charRefs.current = new Array(characters.length);

  useIsomorphicLayoutEffect(() => {
    const targets = charRefs.current.filter(Boolean);
    if (!containerRef.current || targets.length === 0) {
      return;
    }

    CustomWiggle.create(easeName, {
      wiggles,
      type: wiggleType,
    });

    const ctx = gsap.context(() => {
      gsap.timeline({ repeat }).to(targets, {
        duration,
        y: amplitude,
        opacity,
        ease: easeName,
        stagger,
      });
    }, containerRef);

    return () => {
      ctx.revert();
    };
  }, [
    amplitude,
    duration,
    opacity,
    repeat,
    stagger,
    wiggles,
    wiggleType,
    text,
    easeName,
  ]);

  return (
    <p ref={containerRef} className={cn("relative mx-auto py-[10%]")}>
      {characters.map((character, index) => (
        <span
          key={`${character}-${index}`}
          ref={(node) => {
            if (node) {
              charRefs.current[index] = node;
            }
          }}
          className={cn(className, "inline-block")}
        >
          {character === " " ? "\u00A0" : character}
        </span>
      ))}
    </p>
  );
}
